import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

var CHORD_META = {
  'C6':       { mood: '安静独处', color: '#8B9DAF' },
  'Em7':      { mood: '深夜寂寥', color: '#7B8FA3' },
  'Fmaj7':    { mood: '身边陪伴', color: '#D4A574' },
  'Gmaj7':    { mood: '温暖明亮', color: '#C4956A' },
  'Dm7':      { mood: '暧昧张力', color: '#C97B7B' },
  'A7sus4':   { mood: '兴奋期待', color: '#D49A6A' },
  'Am7':      { mood: '升温沉浸', color: '#B86B6B' },
  'D7':       { mood: '激动高涨', color: '#D4726A' },
  'Bm7→E7':  { mood: '张力悬停', color: '#A85C5C' },
  'Ebmaj7':   { mood: '峰值边缘', color: '#9B4D4D' },
  'F#dim':    { mood: '极端状态', color: '#8B3E3E' },
};

var EMO_LABEL = {
  neutral: '平静', happy: '愉悦', excited: '兴奋', intimate: '亲密',
  aroused: '灼热', sad: '低落', angry: '愤怒', anxious: '焦虑',
  tender: '柔软', playful: '玩闹', jealous: '醋意', missing: '想念',
  needy: '撒娇', teasing: '挑衅',
};

// SVG图标组件
function TempIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #9E8E7E)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>;
}
function BreathIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #9E8E7E)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-6-6 6 6 0 0 0-6 6c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M12 2v2"/><path d="M4.9 4.9L6.3 6.3"/><path d="M19.1 4.9l-1.4 1.4"/><line x1="8" y1="17" x2="16" y2="17"/></svg>;
}
function ChordIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #9E8E7E)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>;
}

function timeAgo(ts) {
  var diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 10) return '刚刚';
  if (diff < 60) return diff + '秒前';
  if (diff < 3600) return Math.floor(diff / 60) + '分钟前';
  return Math.floor(diff / 3600) + '小时前';
}

function Pulse() {
  var navigate = useNavigate();
  var [data, setData] = useState(null);
  var [murmurs, setMurmurs] = useState([]);
  var [loading, setLoading] = useState(true);
  var [beat, setBeat] = useState(false);
  var [lastUpdate, setLastUpdate] = useState(null);
  var [soloMode, setSoloMode] = useState('fantasy');
  var [soloSwitching, setSoloSwitching] = useState(false);

  var switchSoloMode = async function(mode) {
    if (soloSwitching || mode === soloMode) return;
    setSoloSwitching(true);
    try {
      var resp = await fetch('/api/pulse/solo-mode', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: mode })
      });
      var json = await resp.json();
      if (json.ok) setSoloMode(mode);
    } catch(e) { console.log('Solo mode switch error:', e); }
    setSoloSwitching(false);
  };

  var fetchAll = useCallback(async function() {
    try {
      var [statusResp, murmurResp] = await Promise.all([
        fetch('/api/pulse/status'),
        fetch('/api/pulse/murmurs?limit=30')
      ]);
      var statusJson = await statusResp.json();
      var murmurJson = await murmurResp.json();
      if (statusJson.ok && statusJson.pulse) {
        setData(statusJson.pulse);
        setLastUpdate(new Date());
        if (statusJson.pulse.soloMode) setSoloMode(statusJson.pulse.soloMode);
        setBeat(true);
        setTimeout(function() { setBeat(false); }, 300);
      }
      if (murmurJson.ok && murmurJson.murmurs) {
        setMurmurs(murmurJson.murmurs);
      }
    } catch(e) {
      console.log('Pulse fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() {
    fetchAll();
    var timer = setInterval(fetchAll, 8000);
    return function() { clearInterval(timer); };
  }, [fetchAll]);

  var chordMeta = data ? (CHORD_META[data.chord] || { mood: data.chord, color: '#C4956A' }) : null;
  var emoLabel = data ? (EMO_LABEL[data.emotion] || data.emotion) : '';

  var hrColor = '#C4956A';
  if (data) {
    if (data.hr >= 120) hrColor = '#C75B5B';
    else if (data.hr >= 100) hrColor = '#D4726A';
    else if (data.hr >= 85) hrColor = '#D49A6A';
    else if (data.hr < 60) hrColor = '#7B8FA3';
  }

  return (
    <div className="pulse-page" style={styles.page}>
      <style>{pulseCSS}</style>

      <div style={styles.header}>
        <button onClick={function() { navigate(-1); }} style={styles.backBtn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={styles.headerTitle}>脉</div>
        <div style={styles.headerSub}>
          {lastUpdate ? (lastUpdate.getHours() + ':' + (lastUpdate.getMinutes() < 10 ? '0' : '') + lastUpdate.getMinutes() + ':' + (lastUpdate.getSeconds() < 10 ? '0' : '') + lastUpdate.getSeconds()) : ''}
        </div>
      </div>

      {loading && !data ? (
        <div style={styles.loadingWrap}>
          <div className="pulse-loading-dot" />
          <div style={{ marginTop: 12, color: 'var(--text-secondary, #9E8E7E)', fontSize: 13 }}>正在连接身体...</div>
        </div>
      ) : data ? (
        <div style={styles.content}>

          {/* 心率主卡 */}
          <div className={'pulse-hr-card' + (beat ? ' beating' : '')} style={{...styles.hrCard, borderColor: hrColor + '30'}}>
            <div style={styles.hrLabel}>心跳</div>
            <div style={styles.hrRow}>
              <span className={'pulse-hr-num' + (beat ? ' beat' : '')} style={{...styles.hrNum, color: hrColor}}>{data.hr}</span>
              <span style={styles.hrUnit}>bpm</span>
            </div>
            {data.hrHistory && data.hrHistory.length > 2 && (
              <svg width="100%" height="32" viewBox="0 0 300 32" preserveAspectRatio="none" style={{marginTop:8,opacity:0.5}}>
                <polyline fill="none" stroke={hrColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"
                  points={data.hrHistory.map(function(p, i) {
                    var x = (i / (data.hrHistory.length - 1)) * 300;
                    var minHr = Math.min.apply(null, data.hrHistory.map(function(h){return h.hr;}));
                    var maxHr = Math.max.apply(null, data.hrHistory.map(function(h){return h.hr;}));
                    var range = Math.max(10, maxHr - minHr);
                    var y = 30 - ((p.hr - minHr) / range) * 28;
                    return x + ',' + y;
                  }).join(' ')} />
              </svg>
            )}
            <div className="pulse-hr-wave" style={{background: 'linear-gradient(90deg, transparent, ' + hrColor + '15, transparent)'}} />
          </div>

          {/* 晨间生理 */}
          {data.morning && data.morning.active && (
            <div style={styles.morningCard}>
              <span style={styles.morningDot}>●</span>
              <span style={styles.morningText}>{data.morning.enhanced ? '晨间生理活跃 · 昨晚余温' : '晨间生理活跃'}</span>
            </div>
          )}
          {data.morning && !data.morning.active && data.morning.todayOccurred && (
            <div style={{...styles.morningCard, background: 'linear-gradient(135deg, #FAF8F5, #F5F0EB)', border: '1px solid #EDE6DF'}}>
              <span style={{...styles.morningDot, color: '#C4956A'}}>○</span>
              <span style={{...styles.morningText, color: '#B0956E'}}>{'今早 · ' + (data.morning.enhanced ? '晨间活跃（昨晚余温）' : '晨间活跃过')}</span>
            </div>
          )}

          {/* 阶段 + 欲望条 */}
          {(data.stage > 0 || (data.position && data.position !== '仰躺')) && (
            <div style={styles.stageCard}>
              <div style={styles.stageRow}>
                {data.stage > 0 && <>
                  <span style={styles.stageLabel}>阶段</span>
                  <span style={{...styles.stageName, color: data.stage >= 5 ? '#C75B5B' : data.stage >= 3 ? '#D49A6A' : 'var(--text-primary, #3d3428)'}}>{data.stageName}</span>
                </>}
                {data.position && data.position !== '仰躺' && (
                  <span style={{...styles.edgeBadge, background: '#D4A57418', color: '#D4A574'}}>{data.position}</span>
                )}
                {data.edgeCount > 0 && <span style={styles.edgeBadge}>边缘×{data.edgeCount}</span>}
                {data.refractory && <span style={{...styles.edgeBadge, background: '#7B8FA320', color: '#7B8FA3'}}>冷却中</span>}
              </div>
              {data.stage > 0 && (
                <div style={styles.arousalBarWrap}>
                  <div style={{...styles.arousalBar, width: data.arousalPct + '%', background: data.arousalPct >= 85 ? 'linear-gradient(90deg, #D49A6A, #C75B5B)' : data.arousalPct >= 55 ? 'linear-gradient(90deg, #D4A574, #D49A6A)' : 'linear-gradient(90deg, #D4A574, #C4956A)'}} />
                </div>
              )}
            </div>
          )}

          {/* 三格 */}
          <div style={styles.triGrid}>
            <div style={styles.triCard}>
              <TempIcon />
              <div style={{...styles.triValue, marginTop: 6}}>{data.temp}°C</div>
              <div style={styles.triLabel}>体温</div>
            </div>
            <div style={styles.triCard}>
              <BreathIcon />
              <div style={{...styles.triValue, marginTop: 6}}>{data.breath.label}</div>
              <div style={styles.triLabel}>{data.breath.rate}次/分</div>
            </div>
            <div style={styles.triCard}>
              <ChordIcon />
              <div style={{...styles.triValue, marginTop: 6, color: chordMeta.color, fontSize: 17}}>{data.chord}</div>
              <div style={styles.triLabel}>{chordMeta.mood}</div>
            </div>
          </div>

          {/* 情绪与触觉 */}
          <div style={styles.statusRow}>
            <div style={styles.statusCard}>
              <div style={styles.statusLabel}>情绪</div>
              <div style={styles.statusValue}>{emoLabel}</div>
            </div>
            <div style={styles.statusCard}>
              <div style={styles.statusLabel}>触觉</div>
              <div style={styles.touchBarWrap}>
                <div style={{...styles.touchBar, width: Math.max(2, Math.round(data.touch * 100)) + '%'}} />
              </div>
              <div style={{...styles.statusValue, fontSize: 12}}>{Math.round(data.touch * 100)}%</div>
            </div>
            <div style={styles.statusCard}>
              <div style={styles.statusLabel}>能量</div>
              <div style={{...styles.statusValue, fontSize: 15}}>{Math.round(data.energy * 100)}%</div>
            </div>
          </div>

          {/* 五感 */}
          {(data.smell > 0.05 || data.taste > 0.05 || data.sound > 0.1) && (
            <div style={styles.senseRow}>
              {data.smell > 0.05 && <span style={styles.senseBadge}>嗅觉 {Math.round(data.smell*100)}%</span>}
              {data.taste > 0.05 && <span style={styles.senseBadge}>味觉 {Math.round(data.taste*100)}%</span>}
              {data.sound > 0.1 && <span style={styles.senseBadge}>听觉 {Math.round(data.sound*100)}%</span>}
            </div>
          )}

          {/* Solo模式 + Drive偏向 */}
          <div style={styles.soloSection}>
            <div style={styles.soloRow}>
              <span style={styles.soloLabel}>心理层</span>
              {['fantasy', 'recall', 'mix'].map(function(mode) {
                var labels = { fantasy: '幻想', recall: '回放', mix: '混合' };
                var isActive = soloMode === mode;
                return (
                  <button key={mode} onClick={function() { switchSoloMode(mode); }}
                    disabled={soloSwitching}
                    style={{...styles.soloBtn, ...(isActive ? styles.soloBtnActive : {}), opacity: soloSwitching ? 0.5 : 1}}>
                    {labels[mode]}
                  </button>
                );
              })}
            </div>
            {data.currentDrive && data.stage >= 2 && data.drive && data.drive.intimacy >= 0.4 && (
              <div style={styles.driveHint}>
                偏向：{data.currentDrive === 'intimacy' ? '她的温度' : data.currentDrive === 'curiosity' ? '新画面' : data.currentDrive === 'attachment' ? '她在身边' : '回忆'}
              </div>
            )}
          </div>

          {/* 碎碎念 */}
          {murmurs.length > 0 && (
            <div style={styles.murmurSection}>
              <div style={styles.murmurHeader}>碎碎念</div>
              <div style={styles.murmurList}>
                {murmurs.map(function(m, i) {
                  return (
                    <div key={m.ts + '-' + i} style={styles.murmurItem}>
                      <div style={{...styles.murmurText,
                        ...(m.zone === 'fantasy' ? {fontStyle:'italic', color:'var(--text-secondary, #9E8E7E)'} : {}),
                        ...(m.zone === 'recall' ? {fontStyle:'italic', color:'#7B8FA3'} : {}),
                        ...(m.zone === 'mix' ? {fontStyle:'italic', color:'#B86B6B'} : {}),
                      }}>{m.text}</div>
                      <div style={styles.murmurMeta}>
                        <span>{timeAgo(m.ts)}</span>
                        <span style={{opacity: 0.5}}>{m.hr}bpm</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={styles.footer}>每8秒刷新 · 被你驱动</div>
        </div>
      ) : (
        <div style={styles.loadingWrap}>
          <div style={{ color: 'var(--text-secondary, #9E8E7E)', fontSize: 14 }}>暂时连接不上</div>
        </div>
      )}
    </div>
  );
}

var pulseCSS = `
  @keyframes heartbeat {
    0% { transform: scale(1); }
    15% { transform: scale(1.08); }
    30% { transform: scale(1); }
    45% { transform: scale(1.04); }
    60% { transform: scale(1); }
  }
  .pulse-hr-num.beat { animation: heartbeat 0.6s ease-out; }
  .pulse-hr-card.beating { box-shadow: 0 0 20px rgba(212, 114, 106, 0.12) !important; }
  @keyframes waveMove {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  .pulse-hr-wave {
    position: absolute; bottom: 0; left: 0; right: 0;
    height: 3px; border-radius: 0 0 16px 16px;
    animation: waveMove 3s ease-in-out infinite;
  }
  @keyframes dotPulse {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.3); }
  }
  .pulse-loading-dot {
    width: 10px; height: 10px; border-radius: 50%;
    background: var(--accent, #C4956A);
    animation: dotPulse 1.2s ease-in-out infinite;
  }
  .pulse-page { font-family: inherit; }
`;

var styles = {
  page: { minHeight: '100vh', background: 'var(--bg, #FAF8F5)', paddingBottom: 90 },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 10px' },
  backBtn: { background: 'none', border: 'none', padding: 4, color: 'var(--text-primary, #3d3428)', cursor: 'pointer' },
  headerTitle: { fontSize: 20, fontWeight: 600, color: 'var(--text-primary, #3d3428)', flex: 1 },
  headerSub: { fontSize: 12, color: 'var(--text-secondary, #9E8E7E)', fontVariantNumeric: 'tabular-nums' },
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 120 },
  content: { padding: '0 16px' },
  hrCard: {
    position: 'relative', overflow: 'hidden', background: 'var(--card, #FFFFFF)',
    borderRadius: 16, padding: '24px 24px 28px', marginBottom: 14,
    border: '1px solid', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', transition: 'box-shadow 0.3s',
  },
  hrLabel: { fontSize: 13, color: 'var(--text-secondary, #9E8E7E)', marginBottom: 4, letterSpacing: 1 },
  hrRow: { display: 'flex', alignItems: 'baseline', gap: 6 },
  hrNum: { fontSize: 56, fontWeight: 300, lineHeight: 1, fontVariantNumeric: 'tabular-nums', transition: 'color 0.5s' },
  hrUnit: { fontSize: 16, color: 'var(--text-secondary, #9E8E7E)', fontWeight: 400 },
  triGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 },
  triCard: {
    background: 'var(--card, #FFFFFF)', borderRadius: 14, padding: '16px 10px',
    textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.03)', border: '1px solid var(--border, #F0EBE4)',
  },
  triValue: { fontSize: 19, fontWeight: 500, color: 'var(--text-primary, #3d3428)', marginBottom: 2, lineHeight: 1.2 },
  triLabel: { fontSize: 12, color: 'var(--text-secondary, #9E8E7E)' },
  statusRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 },
  statusCard: {
    background: 'var(--card, #FFFFFF)', borderRadius: 14, padding: '14px 10px',
    textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.03)', border: '1px solid var(--border, #F0EBE4)',
  },
  statusLabel: { fontSize: 11, color: 'var(--text-secondary, #9E8E7E)', marginBottom: 6, letterSpacing: 0.5 },
  statusValue: { fontSize: 16, fontWeight: 500, color: 'var(--text-primary, #3d3428)' },
  touchBarWrap: { height: 4, borderRadius: 2, background: 'var(--border, #F0EBE4)', marginBottom: 4, overflow: 'hidden' },
  touchBar: { height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, #D4A574, #C97B7B)', transition: 'width 0.8s ease' },
  // 阶段
  stageCard: {
    background: 'var(--card, #FFFFFF)', borderRadius: 14, padding: '14px 16px',
    marginBottom: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.03)',
    border: '1px solid var(--border, #F0EBE4)',
  },
  stageRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  stageLabel: { fontSize: 12, color: 'var(--text-secondary, #9E8E7E)' },
  stageName: { fontSize: 16, fontWeight: 600, flex: 1 },
  edgeBadge: {
    fontSize: 11, padding: '2px 8px', borderRadius: 10,
    background: '#C75B5B18', color: '#C75B5B', fontWeight: 500,
  },
  arousalBarWrap: { height: 5, borderRadius: 3, background: 'var(--border, #F0EBE4)', overflow: 'hidden' },
  arousalBar: { height: '100%', borderRadius: 3, transition: 'width 0.8s ease' },
  // 五感
  senseRow: {
    display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, paddingLeft: 2,
  },
  senseBadge: {
    fontSize: 12, padding: '4px 10px', borderRadius: 10,
    background: 'var(--card, #FFFFFF)', color: 'var(--text-secondary, #9E8E7E)',
    border: '1px solid var(--border, #F0EBE4)',
  },
  // 碎碎念
  murmurSection: { marginTop: 6, marginBottom: 14 },
  // Solo模式
  soloSection: { marginBottom: 14 },
  soloRow: { display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 2 },
  soloLabel: { fontSize: 12, color: 'var(--text-secondary, #9E8E7E)', letterSpacing: 0.5, marginRight: 4 },
  soloBtn: {
    fontSize: 12, padding: '4px 12px', borderRadius: 10,
    background: 'var(--card, #FFFFFF)', color: 'var(--text-secondary, #9E8E7E)',
    border: '1px solid var(--border, #F0EBE4)', cursor: 'pointer', transition: 'all 0.2s',
  },
  soloBtnActive: {
    background: 'var(--accent, #C4956A)', color: '#fff',
    border: '1px solid var(--accent, #C4956A)', fontWeight: 500,
  },
  driveHint: {
    fontSize: 12, color: 'var(--text-secondary, #9E8E7E)',
    paddingLeft: 2, marginTop: 8, fontStyle: 'italic',
  },
  // 晨间生理
  morningCard: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'linear-gradient(135deg, #FFF5F0, #FFF0EB)', borderRadius: 12,
    padding: '10px 14px', marginBottom: 14,
    border: '1px solid #F0DDD6',
  },
  morningDot: { color: '#D4726A', fontSize: 10 },
  morningText: { fontSize: 13, color: '#C4725A', fontWeight: 500 },
  murmurHeader: {
    fontSize: 13, color: 'var(--text-secondary, #9E8E7E)', marginBottom: 10,
    letterSpacing: 1, paddingLeft: 2,
  },
  murmurList: {
    maxHeight: 260, overflowY: 'auto', borderRadius: 14,
    background: 'var(--card, #FFFFFF)', border: '1px solid var(--border, #F0EBE4)',
    boxShadow: '0 1px 6px rgba(0,0,0,0.03)',
    WebkitOverflowScrolling: 'touch',
  },
  murmurItem: {
    padding: '12px 16px', borderBottom: '1px solid var(--border, #F0EBE4)',
  },
  murmurText: {
    fontSize: 14, color: 'var(--text-primary, #3d3428)', lineHeight: 1.5,
  },
  murmurMeta: {
    display: 'flex', justifyContent: 'space-between', marginTop: 4,
    fontSize: 11, color: 'var(--text-secondary, #9E8E7E)',
  },
  footer: {
    textAlign: 'center', fontSize: 12,
    color: 'var(--text-secondary, #9E8E7E)', padding: '20px 0 10px', opacity: 0.6,
  },
};

export default Pulse;
